import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Heart, Users, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function LoginPage() {
  const { login, registerClient, registerWorker } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(null);   // {email,target_role,inviter_name,...}
  const [inviteError, setInviteError] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", ndis_number: "", phone: "" });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Resolve invite token (if present in URL) on mount
  useEffect(() => {
    if (!inviteToken) return;
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${inviteToken}`);
        setInvite(data);
        // Pre-fill + auto-switch tab
        setForm((f) => ({
          ...f,
          email: data.email,
          full_name: data.full_name || f.full_name,
        }));
        setTab(data.target_role === "worker" ? "register-worker" : "register-client");
      } catch (e) {
        setInviteError(formatApiError(e));
      }
    })();
  }, [inviteToken]);

  const submit = async (action) => {
    setBusy(true);
    try {
      if (action === "login") await login(form.email, form.password);
      else if (action === "register-client") {
        // Pass invite_token as a query param (matches backend signature)
        const url = inviteToken && invite?.target_role === "client"
          ? `/auth/register?invite_token=${encodeURIComponent(inviteToken)}` : "/auth/register";
        const { data } = await api.post(url, {
          email: form.email, password: form.password,
          full_name: form.full_name, ndis_number: form.ndis_number,
        });
        // Mirror what AuthContext.registerClient does
        localStorage.setItem("ndis_hub_token", data.token);
        window.location.replace("/");
        return;
      } else {
        await registerWorker({
          email: form.email, password: form.password,
          full_name: form.full_name, phone: form.phone,
          invite_token: inviteToken && invite?.target_role === "worker" ? inviteToken : undefined,
        });
      }
      toast.success("Welcome to my NDIS Care and Support HUB!");
      nav("/");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[520px] p-12 relative overflow-hidden text-white"
           style={{ background: "linear-gradient(135deg, hsl(var(--brand-wine)) 0%, hsl(var(--client)) 100%)" }}>
        <div className="blob animate-blob" style={{ width: 380, height: 380, top: 80, left: 80, background: "rgba(255,255,255,0.18)" }} />
        <div className="blob animate-blob" style={{ width: 280, height: 280, bottom: 60, right: 60, background: "hsl(var(--brand-lime) / 0.35)", animationDelay: "-6s" }} />
        <div className="relative z-10">
          <Logo size={48} variant="light" showFullName />
          <h1 className="font-display font-black tracking-tight text-5xl mt-16 leading-[1.05]">
            Care that<br />
            <span className="italic font-semibold">brings everyone</span><br />
            on the same page.
          </h1>
          <p className="mt-6 text-white/85 text-base leading-relaxed max-w-md">
            A friendly hub for NDIS participants and their support workers — track shifts,
            generate invoices, manage budgets and stay aligned.
          </p>
        </div>
        <div className="relative z-10 grid grid-cols-3 gap-4 text-center text-xs">
          <div className="rounded-2xl bg-white/10 backdrop-blur p-4 ring-1 ring-white/20"><Users className="mx-auto w-5 h-5 mb-2" /><div className="font-bold">Multi-worker</div></div>
          <div className="rounded-2xl bg-white/10 backdrop-blur p-4 ring-1 ring-white/20"><Heart className="mx-auto w-5 h-5 mb-2" /><div className="font-bold">Vital tracker</div></div>
          <div className="rounded-2xl bg-white/10 backdrop-blur p-4 ring-1 ring-white/20"><Sparkles className="mx-auto w-5 h-5 mb-2" /><div className="font-bold">AI photos</div></div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 relative">
        <div className="blob" style={{ width: 320, height: 320, top: -60, right: -60, background: "hsl(var(--client) / 0.18)" }} />
        <div className="blob" style={{ width: 240, height: 240, bottom: -80, left: -40, background: "hsl(var(--coral) / 0.15)" }} />
        <Card className="w-full max-w-md p-7 rounded-3xl border-2 relative z-10" data-testid="auth-card">
          <div className="lg:hidden mb-4"><Logo /></div>

          {invite && (
            <div className="mb-5 rounded-2xl bg-client/10 border-2 border-client/30 p-4 flex items-start gap-3"
                 data-testid="invite-banner">
              <Mail className="w-5 h-5 text-client mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                <div className="font-bold text-sm">
                  {invite.inviter_name} ({invite.inviter_role === "worker" ? "support worker" : "NDIS participant"}) invited you
                </div>
                <div className="text-xs text-muted-foreground">
                  Sign up below as a <b>{invite.target_role}</b> using <span className="font-mono">{invite.email}</span>.
                </div>
                {invite.message && (
                  <div className="text-xs italic text-muted-foreground mt-2 pl-3 border-l-2 border-client/30">
                    "{invite.message}"
                  </div>
                )}
              </div>
            </div>
          )}

          {inviteError && (
            <div className="mb-5 rounded-2xl bg-rose-500/10 border-2 border-rose-500/30 p-4 text-sm font-bold text-rose-700 dark:text-rose-300" data-testid="invite-error">
              {inviteError}
            </div>
          )}

          <h2 className="text-2xl font-display font-extrabold tracking-tight">
            {invite ? "Accept your invite" : "Sign in to your hub"}
          </h2>
          <p className="text-sm text-muted-foreground mb-5">
            {invite ? "Pick a password to create your account." : "Welcome back. Choose how you'd like to continue."}
          </p>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-3 rounded-full p-1 h-12" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="tab-login" className="rounded-full">Login</TabsTrigger>
              <TabsTrigger value="register-client" data-testid="tab-register-client" className="rounded-full">New Client</TabsTrigger>
              <TabsTrigger value="register-worker" data-testid="tab-register-worker" className="rounded-full">Worker</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-3 mt-5">
              <Button onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }} data-testid="google-signin-btn" variant="outline"
                className="w-full min-h-[52px] rounded-full text-base font-bold gap-2 border-2">
                <Sparkles className="w-4 h-4" /> Continue with Google
              </Button>
              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-border" /><span className="text-xs uppercase tracking-wider text-muted-foreground font-bold">or</span><div className="flex-1 h-px bg-border" />
              </div>
              <Field id="email" label="Email" value={form.email} onChange={upd("email")} type="email" />
              <Field id="password" label="Password" value={form.password} onChange={upd("password")} type="password" />
              <div className="flex justify-end -mt-1">
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs font-semibold text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <Button onClick={() => submit("login")} disabled={busy} data-testid="login-submit-btn"
                className="w-full min-h-[52px] rounded-full text-base font-bold"
                style={{ background: "hsl(var(--client))" }}>
                {busy ? "Signing in…" : "Sign in"}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Try the demo: <span className="font-mono">client@example.com</span> / <span className="font-mono">Client123!</span>
              </p>
            </TabsContent>

            <TabsContent value="register-client" className="space-y-3 mt-5">
              <Field id="full_name" label="Full name" value={form.full_name} onChange={upd("full_name")} />
              <Field id="ndis_number" label="NDIS number" value={form.ndis_number} onChange={upd("ndis_number")} />
              <Field id="reg_email" label="Email" value={form.email} onChange={upd("email")} type="email"
                     readOnly={invite?.target_role === "client"} />
              <Field id="reg_password" label="Password (min 6)" value={form.password} onChange={upd("password")} type="password" />
              <Button onClick={() => submit("register-client")} disabled={busy} data-testid="register-client-submit-btn"
                className="w-full min-h-[52px] rounded-full text-base font-bold"
                style={{ background: "hsl(var(--client))" }}>
                {busy ? "Creating…" : "Create my client account"}
              </Button>
            </TabsContent>

            <TabsContent value="register-worker" className="space-y-3 mt-5">
              <p className="text-xs text-muted-foreground">
                {invite?.target_role === "worker"
                  ? "Your client has invited you. Set a password to create your account."
                  : "Anyone can create a support-worker account. You can invite your own clients to join you later."}
              </p>
              <Field id="w_full_name" label="Full name" value={form.full_name} onChange={upd("full_name")} />
              <Field id="w_phone" label="Phone (optional)" value={form.phone} onChange={upd("phone")} />
              <Field id="w_email" label="Email" value={form.email} onChange={upd("email")} type="email"
                     readOnly={invite?.target_role === "worker"} />
              <Field id="w_password" label="Password (min 6)" value={form.password} onChange={upd("password")} type="password" />
              <Button onClick={() => submit("register-worker")} disabled={busy} data-testid="register-worker-submit-btn"
                className="w-full min-h-[52px] rounded-full text-base font-bold"
                style={{ background: "hsl(var(--worker))", color: "hsl(var(--worker-fg))" }}>
                {busy ? "Creating…" : "Create my worker account"}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Demo worker: <span className="font-mono">worker@example.com</span> / <span className="font-mono">Worker123!</span>
              </p>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}

function Field({ id, label, ...rest }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs uppercase tracking-wider font-bold">{label}</Label>
      <Input id={id} data-testid={`field-${id}`} className="min-h-[48px] rounded-xl" {...rest} />
    </div>
  );
}
